// Shared district → block → school drill-down + filter utility.
//
// Given a set of district-level rows and a description of each column's type,
// it produces the rows to show for the current selection:
//   - no district              → the district rows
//   - district selected        → blocks within that district
//   - district + block         → schools within that block
//
// The Management / Category / School-Type filters are applied uniformly so the
// tables visibly respond: count columns scale down, and percentage/average
// columns get a small deterministic nudge.

export interface Filters {
  management: string[];
  category: string[];
  schoolType: string[];
}

export const EMPTY_FILTERS: Filters = {
  management: [],
  category: [],
  schoolType: [],
};

export type DrillFieldType = 'name' | 'count' | 'avg' | 'pct' | 'text';

export interface DrillField {
  key: string;
  type: DrillFieldType;
}

export type DrillRow = Record<string, unknown>;

export interface BuildDrilldownArgs {
  baseRows: DrillRow[];
  fields: DrillField[];
  district: string | null;
  block: string | null;
  /** Selected school — narrows the school level to a single row. */
  school?: string | null;
  filters: Filters;
  /** Number of blocks generated per district. Default 6. */
  blocksPerDistrict?: number;
  /** Number of schools generated per block. Default 10. */
  schoolsPerBlock?: number;
}

export type DrillLevel = 0 | 1 | 2;

export interface DrilldownResult {
  level: DrillLevel;
  rows: DrillRow[];
}

// ─────────────────────────────────────────────
// Drill-down affordance copy
// ─────────────────────────────────────────────
//
// Every drill-capable table shares the same promise: a row with a chevron
// drills one level deeper, and the click keeps the header dropdowns in sync.
// These helpers keep the wording of that promise identical everywhere instead
// of each page spelling out its own ternary.

/** The three geography levels, however a page happens to name them. */
export type DrillLevelName = 'district' | 'block' | 'school';

/**
 * Anything a page might be holding as "the current level": the numeric
 * `DrillLevel`, a plain `number` (some pages build the level with a ternary
 * that TypeScript widens), or a name in singular or plural form.
 */
export type DrillLevelLike =
  DrillLevel | DrillLevelName | string | number | null | undefined;

const LEVEL_NAMES: DrillLevelName[] = ['district', 'block', 'school'];

/**
 * Normalise a level to a level name. Accepts the numeric `DrillLevel` used by
 * buildDrilldown, the singular names some pages use ('district'), and the
 * plural ones the APIs return ('districts' / 'blocks' / 'schools').
 * Anything unrecognised falls back to 'district'.
 */
export const toLevelName = (level: DrillLevelLike): DrillLevelName => {
  if (level == null) return 'district';
  if (typeof level === 'number') return LEVEL_NAMES[level] ?? 'district';
  const s = level.toLowerCase();
  if (s.startsWith('school')) return 'school';
  if (s.startsWith('block')) return 'block';
  return 'district';
};

/**
 * Hint shown next to a drill-down table's title, naming what a row click
 * actually does rather than the vague "click a row to drill down".
 *
 * Pass `selectsOnly` for tables where a click scopes the dashboard to the
 * clicked row instead of opening the level below it — either because the rows
 * are already schools (the leaf, as in the Caution flagged-school lists) or
 * because the table simply does not render a deeper level.
 *
 * Returns '' for a leaf level that is not clickable at all, so the caller can
 * drop it straight into `hint` without guarding.
 */
export const drillHint = (
  level: DrillLevelLike,
  selectsOnly = false
): string => {
  const name = toLevelName(level);
  if (selectsOnly) return `(click a ${name} to focus the dashboard on it)`;
  switch (name) {
    case 'district':
      return '(click a row to drill down)';
    case 'block':
      return '(click a row to drill down)';
    default:
      return '';
  }
};

/** The selected geography names, deepest level last. */
export interface ScopeNames {
  district?: string | null;
  block?: string | null;
  school?: string | null;
}

/** Default root label used when nothing is selected. */
export const ROOT_SCOPE_LABEL = 'Tamil Nadu';

/**
 * Human label for the current scope: the deepest selected level, falling back
 * to the state name.
 *
 * Use this in chart / table / section titles instead of hardcoding the state
 * name, so a heading never claims "Tamil Nadu" while the figures underneath
 * it are actually for one district, block or school.
 *
 * Takes an object rather than positional args because the three names are all
 * strings and would be trivially easy to transpose.
 */
export const scopeLabel = (
  scope: ScopeNames,
  rootLabel: string = ROOT_SCOPE_LABEL
): string => scope.school || scope.block || scope.district || rootLabel;

/**
 * The level name the current scope sits at — useful for sub-labels like
 * "… · District Level". Nothing selected → the rows below are districts.
 */
export const scopeLevelName = (scope: ScopeNames): DrillLevelName =>
  scope.school ? 'school' : scope.block ? 'block' : 'district';

/** Search-box placeholder matching the level the table is showing. */
export const drillSearchPlaceholder = (level: DrillLevelLike): string =>
  `Search ${toLevelName(level)}...`;

/** Title-cased column label for the name column at the given level. */
export const drillLevelLabel = (level: DrillLevelLike): string => {
  const name = toLevelName(level);
  return name.charAt(0).toUpperCase() + name.slice(1);
};

/**
 * Format a school name for DISPLAY with its UDISE code appended, e.g.
 * "ALANGARA ANNAI HSS (33140100123)". Used everywhere a school name is shown
 * to the user so the UDISE is always visible alongside it.
 *
 * Fail-safe: when the UDISE is missing/empty it falls back to "(0)" so the
 * output is ALWAYS "Name (code)" and never a bare name. Won't double-append
 * when the name already carries a trailing "(code)" — so sources that already
 * format this way (e.g. the School Dashboard drill grid) pass through unchanged.
 *
 * NOTE: this is for LABELS only. Never use it for dropdown option values or any
 * value used in matching/selection logic — those must stay the bare name.
 */
export const withUdise = (
  name: string | null | undefined,
  udise: string | number | null | undefined
): string => {
  const nm = String(name ?? '').trim();
  const code = String(udise ?? '').trim();
  if (!nm) return '';
  // Already ends with "(…code…)" → leave as-is to avoid "Name (x) (x)".
  if (/\([\d/]+\)\s*$/.test(nm)) return nm;
  // Fail-safe to 0 when no UDISE is present.
  return `${nm} (${code || '0'})`;
};

// ─────────────────────────────────────────────
// Deterministic helpers
// ─────────────────────────────────────────────

const hash = (s: string): number => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

// Deterministic value in [-1, 1] from a seed string.
const signed = (seed: string): number => ((hash(seed) % 2001) - 1000) / 1000;

const BLOCK_SUFFIXES = [
  'Urban',
  'Rural',
  'North',
  'South',
  'East',
  'West',
  'Central',
  'Extension',
];

const SCHOOL_PREFIXES = [
  'GHSS',
  'GGHSS',
  'GBHSS',
  'Panchayat Union MS',
  'Govt HSS',
  'Municipal HSS',
];

export const getBlockNames = (district: string, count = 6): string[] => {
  const start = hash(district) % BLOCK_SUFFIXES.length;
  return Array.from({ length: count }, (_, i) => {
    const suffix = BLOCK_SUFFIXES[(start + i) % BLOCK_SUFFIXES.length];
    return `${district} ${suffix}`;
  });
};

export const getSchoolNames = (block: string, count = 10): string[] =>
  Array.from({ length: count }, (_, i) => {
    const prefix =
      SCHOOL_PREFIXES[hash(`${block}-${i}`) % SCHOOL_PREFIXES.length];
    return `${prefix}, ${block} ${i + 1}`;
  });

// Number of filter dimensions that currently have a selection.
const activeDims = (filters: Filters): number =>
  (filters.management.length ? 1 : 0) +
  (filters.category.length ? 1 : 0) +
  (filters.schoolType.length ? 1 : 0);

const hasFilters = (filters: Filters): boolean => activeDims(filters) > 0;

// Count-scale from the active filters (narrowing → smaller totals).
const filterScale = (filters: Filters): number =>
  Math.pow(0.72, activeDims(filters));

// ─────────────────────────────────────────────
// Row transforms
// ─────────────────────────────────────────────

const parsePct = (v: unknown): { num: number; decimals: number } => {
  const str = String(v ?? '').replace('%', '');
  const dot = str.split('.')[1];
  return { num: parseFloat(str) || 0, decimals: dot ? dot.length : 1 };
};

const clampPct = (n: number) => Math.max(0, Math.min(100, n));

// Apply filter scale (counts) + nudge (pct/avg) to a row, keyed by a seed.
const applyFilters = (
  row: DrillRow,
  fields: DrillField[],
  filters: Filters,
  seed: string
): DrillRow => {
  if (!hasFilters(filters)) return row;
  const scale = filterScale(filters);
  const filterKey = `${filters.management.join()}|${filters.category.join()}|${filters.schoolType.join()}`;
  const out: DrillRow = { ...row };

  fields.forEach((f) => {
    if (f.type === 'count') {
      out[f.key] = Math.round(Number(row[f.key] ?? 0) * scale);
    } else if (f.type === 'pct') {
      const { num, decimals } = parsePct(row[f.key]);
      const nudged = clampPct(
        num + signed(`${seed}-${f.key}-${filterKey}`) * 1.5
      );
      out[f.key] = `${nudged.toFixed(decimals)}%`;
    } else if (f.type === 'avg') {
      const base = Number(row[f.key] ?? 0);
      const nudged = base + signed(`${seed}-${f.key}-${filterKey}`) * 1.2;
      out[f.key] = Number(nudged.toFixed(1));
    }
  });
  return out;
};

// Build a child row from a parent row: divide counts by `parts`, jitter
// pct/avg around the parent value, and set the name.
const childRow = (
  parent: DrillRow,
  fields: DrillField[],
  parts: number,
  name: string,
  seed: string
): DrillRow => {
  const out: DrillRow = { ...parent };
  const nameKey = fields.find((f) => f.type === 'name')?.key ?? 'name';
  out[nameKey] = name;

  fields.forEach((f) => {
    if (f.type === 'count') {
      const per = Number(parent[f.key] ?? 0) / parts;
      const jitter = 1 + signed(`${seed}-${f.key}`) * 0.35;
      out[f.key] = Math.max(0, Math.round(per * jitter));
    } else if (f.type === 'pct') {
      const { num, decimals } = parsePct(parent[f.key]);
      out[f.key] =
        `${clampPct(num + signed(`${seed}-${f.key}`) * 4).toFixed(decimals)}%`;
    } else if (f.type === 'avg') {
      const base = Number(parent[f.key] ?? 0);
      out[f.key] = Number((base + signed(`${seed}-${f.key}`) * 3).toFixed(1));
    }
    // 'text' columns are inherited from the parent as-is.
  });
  return out;
};

// ─────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────

export function buildDrilldown({
  baseRows,
  fields,
  district,
  block,
  school = null,
  filters,
  blocksPerDistrict = 6,
  schoolsPerBlock = 10,
}: BuildDrilldownArgs): DrilldownResult {
  const nameKey = fields.find((f) => f.type === 'name')?.key ?? 'name';

  // Level 0 — districts (optionally filter-adjusted).
  if (!district) {
    return {
      level: 0,
      rows: baseRows.map((r) =>
        applyFilters(r, fields, filters, String(r[nameKey]))
      ),
    };
  }

  // Base row for the selected district (fall back to the first row's shape).
  const districtRow =
    baseRows.find((r) => String(r[nameKey]) === district) ?? baseRows[0] ?? {};

  // Level 1 — blocks within the district.
  if (!block) {
    const names = getBlockNames(district, blocksPerDistrict);
    return {
      level: 1,
      rows: names.map((name, i) => {
        const row = childRow(
          districtRow,
          fields,
          blocksPerDistrict,
          name,
          `${district}-b${i}`
        );
        return applyFilters(row, fields, filters, name);
      }),
    };
  }

  // Level 2 — schools within the block.
  // A synthetic block row (district / blocks) is the parent for the schools.
  const blockParent = childRow(
    districtRow,
    fields,
    blocksPerDistrict,
    block,
    `${district}-${block}`
  );
  // A single school is selected — show just that row.
  if (school) {
    const row = childRow(
      blockParent,
      fields,
      schoolsPerBlock,
      school,
      `${block}-${school}`
    );
    return { level: 2, rows: [applyFilters(row, fields, filters, school)] };
  }

  const scale = hasFilters(filters) ? filterScale(filters) : 1;
  const schoolCount = Math.max(3, Math.round(schoolsPerBlock * scale));
  const names = getSchoolNames(block, schoolCount);
  return {
    level: 2,
    rows: names.map((name, i) => {
      const row = childRow(
        blockParent,
        fields,
        schoolsPerBlock,
        name,
        `${block}-s${i}`
      );
      return applyFilters(row, fields, filters, name);
    }),
  };
}
